import { useNostr } from '@nostrify/react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';

import { getImageUrl } from '@/lib/polaroid-utils';
import { isValidAlbum } from '@/lib/album-utils';

/** Validate a feed event — kind 20 with valid image or kind 30006 album (no blocklist for following feed). */
function isValidFeedEvent(event: NostrEvent): boolean {
  if (event.kind === 30006) return isValidAlbum(event);
  if (event.kind !== 20) return false;
  return !!getImageUrl(event);
}

const PAGE_SIZE = 15;

/** Fetch the user's kind 3 follow list and return followed pubkeys. */
export function useFollowList(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['follow-list', pubkey ?? ''],
    queryFn: async () => {
      if (!pubkey) return [];

      const events = await nostr.query(
        [{ kinds: [3], authors: [pubkey], limit: 1 }],
        { signal: AbortSignal.timeout(5000) },
      );

      if (events.length === 0) return [];

      // Extract pubkeys from p tags
      const followedPubkeys = events[0].tags
        .filter(([name]) => name === 'p')
        .map(([, followedPubkey]) => followedPubkey)
        .filter((pk): pk is string => !!pk);

      return followedPubkeys;
    },
    enabled: !!pubkey,
    staleTime: 5 * 60 * 1000, // 5 minutes — follow list doesn't change often
  });
}

/** Query kind 20 picture events from followed authors with infinite scroll pagination. */
export function useFollowingPictures(pubkey: string | undefined) {
  const { nostr } = useNostr();
  const { data: followedPubkeys, isLoading: isLoadingFollows } = useFollowList(pubkey);

  const hasFollows = !!followedPubkeys && followedPubkeys.length > 0;

  const infiniteQuery = useInfiniteQuery({
    queryKey: ['pictures', 'following', pubkey ?? ''],
    queryFn: async ({ pageParam }) => {
      if (!followedPubkeys || followedPubkeys.length === 0) return [];

      // Include the user's own pictures in the feed too
      const authors = pubkey ? [...followedPubkeys, pubkey] : followedPubkeys;

      const filter: NostrFilter = { kinds: [20, 30006], authors, limit: PAGE_SIZE + 5 };
      if (pageParam) {
        filter.until = pageParam;
      }

      const events = await nostr.query(
        [filter],
        { signal: AbortSignal.timeout(5000) },
      );

      // No blocklist filtering — the user chose to follow these people
      return events.filter(isValidFeedEvent).slice(0, PAGE_SIZE);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length === 0) return undefined;
      return lastPage[lastPage.length - 1].created_at - 1;
    },
    initialPageParam: undefined as number | undefined,
    enabled: hasFollows,
    staleTime: 60 * 1000,
  });

  return {
    ...infiniteQuery,
    // Surface whether the follow list is still loading so the Feed page can decide
    isLoadingFollows,
    hasFollows,
  };
}
